import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import {
  SparklesIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  MicrophoneIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ShieldExclamationIcon,
  CheckIcon,
  UserCircleIcon,
  ChartBarIcon,
  BanknotesIcon,
  ScaleIcon,
  TrophyIcon,
  ArrowsRightLeftIcon,
  UsersIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  ArrowTopRightOnSquareIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  WrenchScrewdriverIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import {
  isAIAssistantUser,
  isAIAssistantUnlocked,
  AI_ASSISTANT_UNLOCK_EVENT,
  answerQuestion,
  detectLanguage
} from '../services/aiAssistantService';
import {
  sendChat,
  sendVoice,
  synthesizeSpeech,
  executeTool,
  playBase64Audio,
  PcmRecorder,
  MAX_RECORD_SECONDS,
  MAX_ATTACHMENTS,
  isImageFile,
  prepareAttachments,
  imagesFromDataTransfer
} from '../services/aiChatService';

const WELCOME_MESSAGE = {
  role: 'assistant',
  text: "Hi! I'm your panel assistant. Ask me anything — revenue, limits, leagues, players, transactions — or ask me to set a limit and I'll prepare it for your approval. You can also attach a screenshot and ask about what's in it. Ahla! Fik tes2alne bel lebnene kamen, kitebe aw bel mic.",
  lang: 'en'
};

const formatNum = (n) => {
  const num = parseFloat(n) || 0;
  return Math.abs(num) >= 1000 ? num.toLocaleString('en-US', { maximumFractionDigits: 0 }) : num.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/* ============================== Visual cards ============================== */

// Tailwind needs full class names at build time, so accents are a static map
const CARD_ACCENTS = {
  indigo: {
    border: 'border-indigo-200 dark:border-indigo-800',
    header: 'bg-indigo-50 dark:bg-indigo-900/30',
    icon: 'text-indigo-500',
    title: 'text-indigo-600 dark:text-indigo-300'
  },
  emerald: {
    border: 'border-emerald-200 dark:border-emerald-800',
    header: 'bg-emerald-50 dark:bg-emerald-900/30',
    icon: 'text-emerald-500',
    title: 'text-emerald-600 dark:text-emerald-300'
  },
  amber: {
    border: 'border-amber-200 dark:border-amber-800',
    header: 'bg-amber-50 dark:bg-amber-900/30',
    icon: 'text-amber-500',
    title: 'text-amber-600 dark:text-amber-300'
  },
  purple: {
    border: 'border-purple-200 dark:border-purple-800',
    header: 'bg-purple-50 dark:bg-purple-900/30',
    icon: 'text-purple-500',
    title: 'text-purple-600 dark:text-purple-300'
  },
  sky: {
    border: 'border-sky-200 dark:border-sky-800',
    header: 'bg-sky-50 dark:bg-sky-900/30',
    icon: 'text-sky-500',
    title: 'text-sky-600 dark:text-sky-300'
  },
  rose: {
    border: 'border-rose-200 dark:border-rose-800',
    header: 'bg-rose-50 dark:bg-rose-900/30',
    icon: 'text-rose-500',
    title: 'text-rose-600 dark:text-rose-300'
  }
};

const CardShell = ({ icon: Icon, title, children, accent = 'indigo' }) => {
  const c = CARD_ACCENTS[accent] || CARD_ACCENTS.indigo;
  return (
    <div className={`w-full rounded-2xl border ${c.border} bg-white dark:bg-gray-800 overflow-hidden shadow-sm`}>
      <div className={`flex items-center gap-2 px-3.5 py-2 ${c.header}`}>
        <Icon className={`w-5 h-5 ${c.icon}`} />
        <span className={`text-xs font-bold uppercase tracking-wide ${c.title}`}>{title}</span>
      </div>
      <div className="px-3.5 py-3">{children}</div>
    </div>
  );
};

const GgrCard = ({ data }) => (
  <div className="w-full rounded-2xl overflow-hidden shadow-md bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 text-white">
    <div className="px-4 pt-3 pb-1 flex items-center gap-2">
      <ChartBarIcon className="w-4 h-4 opacity-80" />
      <span className="text-[11px] font-bold uppercase tracking-wider opacity-80">GGR · {data.currency}</span>
    </div>
    <div className="px-4 pb-1">
      <span className="text-3xl font-extrabold tracking-tight">{formatNum(data.totals?.ggr)}</span>
      <span className="ml-2 text-sm font-medium opacity-80">{data.currency}</span>
    </div>
    <div className="px-4 pb-3 grid grid-cols-3 gap-2 text-center">
      {[['NGR', data.totals?.ngr], ['Turnover', data.totals?.turnover], ['Bets', data.totals?.total_bets]].map(([label, value]) => (
        <div key={label} className="rounded-lg bg-white/15 py-1.5">
          <div className="text-[10px] uppercase tracking-wide opacity-75">{label}</div>
          <div className="text-sm font-bold">{formatNum(value)}</div>
        </div>
      ))}
    </div>
    {data.period && (
      <div className="px-4 pb-2.5 text-[10px] opacity-70">
        {String(data.period.start).slice(0, 10)} → {String(data.period.end).slice(0, 10)}
      </div>
    )}
  </div>
);

const BalancesCard = ({ data }) => (
  <CardShell icon={BanknotesIcon} title={`Balances · ${data.username}`} accent="emerald">
    {Object.keys(data.balances || {}).length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">No wallets found</p>
    ) : (
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.balances).map(([currency, amount]) => (
          <span key={currency} className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800">
            <span className="text-base font-bold text-emerald-700 dark:text-emerald-300">{formatNum(amount)}</span>
            <span className="text-[11px] font-semibold text-emerald-500">{currency}</span>
          </span>
        ))}
      </div>
    )}
  </CardShell>
);

const LimitsCard = ({ data }) => (
  <CardShell icon={ScaleIcon} title={`Bet limits · ${data.username}`} accent="amber">
    {(data.limits || []).length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">No limits configured</p>
    ) : (
      <ul className="space-y-1.5">
        {(data.limits || []).slice(0, 6).map((l) => (
          <li key={l.id} className="flex items-start gap-2 text-[13px] text-gray-700 dark:text-gray-200">
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${l.active ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
            <span className="leading-snug">{l.summary}</span>
          </li>
        ))}
        {(data.limits || []).length > 6 && (
          <li className="text-xs text-gray-400">+{data.limits.length - 6} more…</li>
        )}
      </ul>
    )}
  </CardShell>
);

const TopPlayersCard = ({ data }) => (
  <CardShell icon={TrophyIcon} title={`Top players · ${data.currency}`} accent="purple">
    <ol className="space-y-1">
      {(data.top_players || []).slice(0, 8).map((p, i) => (
        <li key={p.username} className="flex items-center gap-2.5 text-[13px]">
          <span className={`w-5 h-5 shrink-0 flex items-center justify-center rounded-full text-[10px] font-bold ${
            i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-amber-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300'
          }`}>{i + 1}</span>
          <span className="font-medium text-gray-800 dark:text-gray-100 truncate flex-1">{p.username}</span>
          <span className="font-bold text-purple-600 dark:text-purple-300 tabular-nums">{formatNum(p.ggr)}</span>
        </li>
      ))}
    </ol>
  </CardShell>
);

const TransactionsCard = ({ data }) => (
  <CardShell icon={ArrowsRightLeftIcon} title={`Transactions · ${data.username}`} accent="sky">
    {Object.keys(data.totals_by_currency || {}).length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">No transactions in this period</p>
    ) : (
      <div className="space-y-2">
        {Object.entries(data.totals_by_currency).map(([currency, t]) => (
          <div key={currency} className="flex items-center gap-2 text-[13px]">
            <span className="w-10 shrink-0 font-bold text-gray-500 dark:text-gray-400">{currency}</span>
            <span className="px-2 py-0.5 rounded-md bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-semibold">+{formatNum(t.deposits)}</span>
            <span className="px-2 py-0.5 rounded-md bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 font-semibold">−{formatNum(t.withdrawals)}</span>
            <span className="ml-auto text-xs text-gray-400">{t.count} txns</span>
          </div>
        ))}
      </div>
    )}
  </CardShell>
);

const DescendantsCard = ({ data }) => (
  <CardShell icon={UsersIcon} title={`Network · ${data.username}`} accent="indigo">
    <div className="flex items-baseline gap-2 mb-2">
      <span className="text-2xl font-extrabold text-indigo-600 dark:text-indigo-300">{formatNum(data.total_descendants)}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">total users{data.truncated ? ' (truncated)' : ''}</span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(data.counts_by_type || {}).map(([type, count]) => (
        <span key={type} className="px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 text-xs">
          <span className="font-bold text-indigo-700 dark:text-indigo-300">{formatNum(count)}</span>
          <span className="ml-1 text-gray-500 dark:text-gray-400">{type}</span>
        </span>
      ))}
    </div>
  </CardShell>
);

const UsersWithLimitsCard = ({ data }) => (
  <CardShell icon={ScaleIcon} title="Users with limits" accent="amber">
    <ul className="space-y-1.5">
      {(data.users || []).slice(0, 8).map((u) => (
        <li key={u.username} className="flex items-center gap-2 text-[13px]">
          <span className="font-medium text-gray-800 dark:text-gray-100 truncate flex-1">{u.username}</span>
          <span className="text-xs px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-semibold">
            {u.active_limits} active
          </span>
        </li>
      ))}
      {(data.users || []).length > 8 && <li className="text-xs text-gray-400">+{data.users.length - 8} more…</li>}
    </ul>
  </CardShell>
);

const OverviewCard = ({ data }) => (
  <CardShell icon={UserCircleIcon} title={`Overview · ${data.username}`} accent="indigo">
    <div className="space-y-2.5">
      {data.profile?.found && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-semibold">{data.profile.type}</span>
          {data.profile.parent && <span className="text-gray-500 dark:text-gray-400">under {data.profile.parent}</span>}
          {data.profile.blocked && <span className="px-2 py-0.5 rounded-md bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-300 font-bold">BLOCKED</span>}
        </div>
      )}
      {data.balances?.balances && Object.keys(data.balances.balances).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(data.balances.balances).map(([currency, amount]) => (
            <span key={currency} className="px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-xs font-bold text-emerald-700 dark:text-emerald-300">
              {formatNum(amount)} {currency}
            </span>
          ))}
        </div>
      )}
      {(data.bet_limits?.limits || []).length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {data.bet_limits.limits.length} bet limit{data.bet_limits.limits.length > 1 ? 's' : ''} configured
        </p>
      )}
    </div>
  </CardShell>
);

const CountrySummaryCard = ({ data }) => (
  <CardShell icon={GlobeAltIcon} title={`${data.name || data.username} · ${data.type}`} accent="indigo">
    <div className="space-y-2.5">
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(data.network?.counts_by_type || {}).map(([type, count]) => (
          <span key={type} className="px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-100 dark:border-indigo-800 text-xs">
            <span className="font-bold text-indigo-700 dark:text-indigo-300">{formatNum(count)}</span>
            <span className="ml-1 text-gray-500 dark:text-gray-400">{type}</span>
          </span>
        ))}
      </div>
      {Object.keys(data.month_to_date?.revenue || {}).length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">Month to date</div>
          {Object.entries(data.month_to_date.revenue).map(([currency, totals]) => (
            <div key={currency} className="flex items-center gap-2 text-[13px]">
              <span className="w-10 shrink-0 font-bold text-gray-500 dark:text-gray-400">{currency}</span>
              <span className="font-bold text-gray-800 dark:text-gray-100">{formatNum(totals.ggr)}</span>
              <span className="text-xs text-gray-400">GGR</span>
              <span className="ml-auto text-xs text-gray-400">{formatNum(totals.turnover)} turnover</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-3 text-xs text-gray-500 dark:text-gray-400">
        <span>{formatNum(data.users_with_limits)} with limits</span>
        <span>{formatNum(data.blocked_users)} blocked</span>
      </div>
    </div>
  </CardShell>
);

const ComparisonCard = ({ data }) => {
  const rows = [
    ['GGR', data.current?.ggr, data.previous?.ggr, data.comparison?.ggr],
    ['Turnover', data.current?.turnover, data.previous?.turnover, data.comparison?.turnover],
    ['Bets', data.current?.total_bets, data.previous?.total_bets, data.comparison?.total_bets]
  ];
  return (
    <CardShell icon={ChartBarIcon} title={`Comparison · ${data.currency}`} accent="purple">
      <div className="space-y-1.5">
        {rows.map(([label, current, previous, delta]) => {
          const up = (delta?.change || 0) >= 0;
          return (
            <div key={label} className="flex items-center gap-2 text-[13px]">
              <span className="w-16 shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
              <span className="font-bold text-gray-800 dark:text-gray-100 tabular-nums">{formatNum(current)}</span>
              <span className="text-xs text-gray-400 tabular-nums">from {formatNum(previous)}</span>
              <span className={`ml-auto text-xs font-bold tabular-nums ${up ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {up ? '+' : ''}{formatNum(delta?.change)}
                {delta?.percent_change !== null && delta?.percent_change !== undefined && ` (${up ? '+' : ''}${delta.percent_change}%)`}
              </span>
            </div>
          );
        })}
      </div>
    </CardShell>
  );
};

/** Which limit actually bites for one scope, and where it comes from. */
const EffectiveLimitCard = ({ data }) => (
  <CardShell icon={ScaleIcon} title={`Effective limit · ${data.scope?.label || 'scope'}`} accent="amber">
    {data.effective_limit ? (
      <div className="space-y-1.5">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-extrabold text-amber-700 dark:text-amber-300">
            {formatNum(data.effective_limit.max_bet)}
          </span>
          <span className="text-xs font-semibold text-amber-500">{data.currency} max bet</span>
        </div>
        <p className="text-[13px] text-gray-600 dark:text-gray-300">
          from the {data.effective_limit.source} on {data.effective_limit.set_on}
        </p>
        {(data.all_applicable || []).length > 1 && (
          <p className="text-[11px] text-gray-400">
            {data.all_applicable.length - 1} broader limit{data.all_applicable.length > 2 ? 's' : ''} also apply
          </p>
        )}
      </div>
    ) : (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        No limit on {data.scope?.label} in {data.currency} — not on {data.username} and not above them.
      </p>
    )}
  </CardShell>
);

/** What the limit audit found under a country, city or agent. */
const LimitConflictsCard = ({ data }) => {
  const groups = [
    ['Looser than the level above', data.looser_than_ancestor, 'text-red-600 dark:text-red-400'],
    ['Min above max (blocks all bets)', data.impossible_limits, 'text-red-600 dark:text-red-400'],
    ['League no longer in the catalog', data.orphaned_scopes, 'text-amber-600 dark:text-amber-400'],
    ['Redundant duplicates', data.redundant_limits, 'text-gray-500 dark:text-gray-400']
  ].filter(([, rows]) => (rows || []).length > 0);

  return (
    <CardShell icon={ScaleIcon} title={`Limit audit · ${data.root_username}`} accent={data.problem_count > 0 ? 'rose' : 'emerald'}>
      <p className="text-[13px] text-gray-600 dark:text-gray-300">
        {data.limits_checked} limits across {data.users_checked} users
      </p>
      {groups.length === 0 ? (
        <p className="mt-1.5 text-sm font-semibold text-green-700 dark:text-green-400">Nothing broken</p>
      ) : (
        <div className="mt-2 space-y-2">
          {groups.map(([label, rows, tone]) => (
            <div key={label}>
              <p className={`text-[11px] font-bold uppercase tracking-wide ${tone}`}>
                {label} · {rows.length}
              </p>
              <div className="mt-0.5 space-y-0.5">
                {rows.slice(0, 4).map((row, i) => (
                  <p key={i} className="text-[12px] text-gray-600 dark:text-gray-300 truncate">
                    {row.username} — {row.problem}
                  </p>
                ))}
                {rows.length > 4 && (
                  <p className="text-[11px] text-gray-400">and {rows.length - 4} more</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </CardShell>
  );
};

/** Leagues ranked by what they earned or lost. */
const LeaguePerformanceCard = ({ data }) => (
  <CardShell icon={TrophyIcon} title={`Leagues · ${data.filters?.currency || 'all'}`} accent="sky">
    {(data.leagues || []).length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">{data.message || 'No league activity.'}</p>
    ) : (
      <>
        <div className="space-y-1">
          {data.leagues.slice(0, 8).map((row, i) => (
            <div key={i} className="flex items-baseline gap-2 text-[13px]">
              <span className="flex-1 min-w-0 truncate text-gray-700 dark:text-gray-200">
                {row.league}
                <span className="text-gray-400"> · {row.sport}</span>
              </span>
              <span className={`shrink-0 font-bold tabular-nums ${row.ggr < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>
                {formatNum(row.ggr)}
              </span>
              <span className="shrink-0 w-12 text-right text-[11px] text-gray-400 tabular-nums">
                {row.hold_percent === null ? '—' : `${row.hold_percent}%`}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-2 text-[11px] text-gray-400">
          {data.league_count} leagues
          {data.losing_league_count > 0 && ` · ${data.losing_league_count} losing`}
          {data.data_freshness?.minutes_behind > 90 && ' · numbers lag live data'}
        </p>
      </>
    )}
  </CardShell>
);

/** Casino vs sportsbook, or a ranked game/provider/sportsbook breakdown. */
const PlatformAnalyticsCard = ({ data }) => (
  <CardShell icon={ChartBarIcon} title={`Platform · ${data.breakdown || 'summary'}`} accent="purple">
    {data.by_currency ? (
      <div className="space-y-1.5">
        {data.by_currency.map((row) => (
          <div key={row.currency} className="flex items-baseline gap-2 text-[13px]">
            <span className="w-10 shrink-0 text-xs font-bold text-gray-500 dark:text-gray-400">{row.currency}</span>
            <span className="text-gray-500 dark:text-gray-400">casino</span>
            <span className="font-bold text-gray-800 dark:text-gray-100 tabular-nums">{formatNum(row.casino?.ggr)}</span>
            <span className="ml-auto text-gray-500 dark:text-gray-400">sports</span>
            <span className="font-bold text-gray-800 dark:text-gray-100 tabular-nums">{formatNum(row.sports?.ggr)}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="space-y-1">
        {(data.rows || []).slice(0, 8).map((row, i) => (
          <div key={i} className="flex items-baseline gap-2 text-[13px]">
            <span className="flex-1 min-w-0 truncate text-gray-700 dark:text-gray-200">
              {row.game || row.provider || row.sportsbook}
              <span className="text-gray-400"> · {row.currency}</span>
            </span>
            <span className={`shrink-0 font-bold tabular-nums ${row.ggr < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>
              {formatNum(row.ggr)}
            </span>
          </div>
        ))}
      </div>
    )}
    {data.message && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{data.message}</p>}
  </CardShell>
);

/** Day-by-day revenue, with the losing days called out. */
const TimeseriesCard = ({ data }) => {
  const days = data.days || [];
  const peak = Math.max(1, ...days.map((day) => Math.abs(day.ggr)));

  return (
    <CardShell icon={ChartBarIcon} title={`Daily GGR · ${data.currency}`} accent="emerald">
      {days.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{data.message || 'No activity.'}</p>
      ) : (
        <>
          <div className="flex items-end gap-[3px] h-16">
            {days.slice(-45).map((day, i) => (
              <div key={i} className="flex-1 flex flex-col justify-end h-full" title={`${day.date}: ${formatNum(day.ggr)}`}>
                <span
                  className={`w-full rounded-sm ${day.ggr < 0 ? 'bg-red-400' : 'bg-emerald-400'}`}
                  style={{ height: `${Math.max(2, (Math.abs(day.ggr) / peak) * 100)}%` }}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
            <span>avg {formatNum(data.average_daily_ggr)}/day</span>
            <span className="text-emerald-600 dark:text-emerald-400">best {data.best_day?.date} ({formatNum(data.best_day?.ggr)})</span>
            {(data.losing_days || []).length > 0 && (
              <span className="text-red-600 dark:text-red-400">{data.losing_days.length} losing day{data.losing_days.length > 1 ? 's' : ''}</span>
            )}
          </div>
        </>
      )}
    </CardShell>
  );
};

const VISUAL_RENDERERS = {
  get_ggr: GgrCard,
  get_effective_limit: EffectiveLimitCard,
  find_limit_conflicts: LimitConflictsCard,
  get_league_performance: LeaguePerformanceCard,
  get_platform_analytics: PlatformAnalyticsCard,
  get_revenue_timeseries: TimeseriesCard,
  get_user_balance: BalancesCard,
  get_bet_limits: LimitsCard,
  get_top_players: TopPlayersCard,
  get_transactions: TransactionsCard,
  count_descendants: DescendantsCard,
  get_users_with_limits: UsersWithLimitsCard,
  get_user_overview: OverviewCard,
  get_country_summary: CountrySummaryCard,
  get_ggr_comparison: ComparisonCard
};

/* ============================== Voice ============================== */

const WAVEFORM_BARS = 32;

/**
 * Live microphone waveform.
 *
 * Reads the current level from a ref on an animation frame instead of from
 * component state, so amplitude updates never re-render the conversation.
 */
const VoiceWaveform = ({ levelRef }) => {
  const [bars, setBars] = useState(() => new Array(WAVEFORM_BARS).fill(0));

  useEffect(() => {
    let frame;
    let last = 0;
    let history = new Array(WAVEFORM_BARS).fill(0);

    const tick = (time) => {
      // ~25fps is plenty for a waveform and keeps the main thread free
      if (time - last > 40) {
        last = time;
        history = [...history.slice(1), levelRef.current || 0];
        setBars(history);
      }
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [levelRef]);

  return (
    <div className="flex items-center justify-center gap-[2px] h-6 flex-1" aria-hidden="true">
      {bars.map((level, i) => (
        <span
          key={i}
          className="w-[3px] rounded-full bg-red-500 transition-[height] duration-75"
          style={{ height: `${Math.max(3, Math.min(24, level * 24 + 3))}px`, opacity: 0.45 + level * 0.55 }}
        />
      ))}
    </div>
  );
};

/* ============================== Conversation state ============================== */

const STORAGE_KEY = 'panelAssistant.conversation.v1';
const MAX_STORED_MESSAGES = 60;

/** Restore the conversation so navigating around the panel does not lose it. */
const loadConversation = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    if (Array.isArray(stored) && stored.length > 0) return stored;
  } catch {
    /* corrupt or unavailable storage — start fresh */
  }
  return [WELCOME_MESSAGE];
};

/**
 * An action the admin never confirmed must not survive a reload: the figures
 * behind it are stale, so it comes back as cancelled rather than actionable.
 *
 * Attached images are dropped before storing — a couple of screenshots would
 * blow the localStorage quota on their own — so a restored turn only remembers
 * that images were there.
 */
const saveConversation = (messages) => {
  try {
    const safe = messages.slice(-MAX_STORED_MESSAGES).map((msg) => {
      const withoutImages = (msg.images || []).length > 0
        ? { ...msg, images: undefined, imageCount: msg.images.length }
        : msg;
      return withoutImages.type === 'confirm' && (withoutImages.status === 'pending' || withoutImages.status === 'working')
        ? { ...withoutImages, status: 'cancelled' }
        : withoutImages;
    });
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
  } catch {
    /* quota or private mode — persistence is a nicety, not a requirement */
  }
};

const STARTER_PROMPTS = [
  { label: 'GGR this month', prompt: "What is the platform GGR this month?" },
  { label: 'Which leagues lose money?', prompt: 'Which sports leagues are losing us money this month, and which of them can I put a limit on?' },
  { label: 'Casino vs sports', prompt: 'How did casino and sportsbook each do this month, per currency?' },
  { label: 'Which countries exist?', prompt: 'List all the countries in the tree with their usernames.' },
  { label: 'Check my limits', prompt: 'Audit the bet limits in the tree and tell me if anything is wrong with them.' },
  { label: 'Top players', prompt: 'Show me the top players this month in USD.' }
];

/** Contextual next steps, derived from what the assistant just did. */
const followUpsFor = (msg) => {
  if (!msg || msg.role !== 'assistant' || msg.type === 'confirm') return [];

  const chips = [];
  const tools = msg.toolsUsed || [];
  const subject = (msg.entities || [])[0];

  if (subject) {
    chips.push({ label: `Limits of ${subject.username}`, prompt: `What bet limits apply to ${subject.username} and where do they come from?` });
    if (!tools.includes('get_user_overview')) {
      chips.push({ label: `Deep view of ${subject.username}`, prompt: `Give me the full deep view of ${subject.username}.` });
    }
  }
  if (tools.includes('list_sports_events') || tools.includes('get_effective_limit')) {
    chips.push({ label: 'Limit a league', prompt: 'Walk me through putting a max bet on one league, and tell me what is already in force.' });
  }
  if (tools.includes('get_bet_limits') && !tools.includes('find_limit_conflicts')) {
    chips.push({ label: 'Audit these limits', prompt: 'Check those limits for problems: anyone allowed to bet more than the level above them, or limits that can never be met.' });
  }
  if (tools.includes('get_ggr') && !tools.includes('get_ggr_comparison')) {
    chips.push({ label: 'Compare to last month', prompt: 'Compare those figures to the same period last month.' });
  }
  if ((tools.includes('get_ggr') || tools.includes('get_platform_analytics')) && !tools.includes('get_revenue_timeseries')) {
    chips.push({ label: 'Break it down by day', prompt: 'Show that day by day so I can see when it changed.' });
  }
  if (tools.includes('get_league_performance')) {
    chips.push({ label: 'Limit the worst league', prompt: 'Put a max bet on the league that lost the most, and tell me what is already in force on it.' });
  }
  if (tools.includes('get_revenue_timeseries')) {
    chips.push({ label: 'What caused the worst day?', prompt: 'Dig into the worst day: which league or game, and which players were behind it?' });
  }
  if (tools.includes('list_countries')) {
    chips.push({ label: 'Summarize a country', prompt: 'Give me a summary of the biggest country in the tree.' });
  }
  if (tools.includes('create_sports_event')) {
    chips.push({ label: 'Limit the new league', prompt: 'Now set a max bet on the league you just added.' });
  }

  return chips.slice(0, 3);
};

const MIC_LANGS = [
  { id: 'auto', label: 'AUTO', title: 'Detect language automatically' },
  { id: 'en', label: 'EN', title: 'English' },
  { id: 'ar', label: 'ع', title: 'Lebanese Arabic' }
];

/* ============================== Main widget ============================== */

const AIAssistant = () => {
  const [allowed, setAllowed] = useState(false);
  const [unlocked, setUnlocked] = useState(isAIAssistantUnlocked);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(loadConversation);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [cancelArmed, setCancelArmed] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [micLang, setMicLang] = useState('auto');
  const [attachments, setAttachments] = useState([]);
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachNote, setAttachNote] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [preview, setPreview] = useState(null);

  const navigate = useNavigate();
  const recorderRef = useRef(null);
  const playingAudioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordTimerRef = useRef(null);
  const levelRef = useRef(0);
  const pressRef = useRef(null);
  const cancelArmedRef = useRef(false);
  cancelArmedRef.current = cancelArmed;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const voiceEnabledRef = useRef(voiceEnabled);
  voiceEnabledRef.current = voiceEnabled;
  const micLangRef = useRef(micLang);
  micLangRef.current = micLang;
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  // Gate: only the allowed admin, and only after the profile-picture unlock
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await getCurrentUser();
        if (!cancelled && isAIAssistantUser(user?.username)) {
          setAllowed(true);
        }
      } catch { /* not signed in — keep hidden */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onUnlock = () => setUnlocked(true);
    window.addEventListener(AI_ASSISTANT_UNLOCK_EVENT, onUnlock);
    return () => window.removeEventListener(AI_ASSISTANT_UNLOCK_EVENT, onUnlock);
  }, []);

  const stopPlayback = useCallback(() => {
    playingAudioRef.current?.pause();
    playingAudioRef.current = null;
    window.speechSynthesis?.cancel();
  }, []);

  /** Play a server audio reply; fall back to browser TTS if no audio available */
  const speakReply = useCallback((base64Audio, text, lang, mime) => {
    if (!voiceEnabledRef.current) return;
    stopPlayback();
    if (base64Audio) {
      playingAudioRef.current = playBase64Audio(base64Audio, mime || 'audio/mp3');
      return;
    }
    if (window.speechSynthesis && text) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang === 'ar' ? 'ar-SA' : 'en-US';
      window.speechSynthesis.speak(utterance);
    }
  }, [stopPlayback]);

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  /** History in the shape the backend expects */
  const historyForBackend = useCallback(() => {
    const turns = messagesRef.current
      .filter((m) => !m.isError && m.type !== 'confirm' && (m.text || (m.images || []).length > 0))
      .slice(1) // drop welcome message
      .map((m) => ({ role: m.role, text: m.text || '', images: m.images || [] }));

    // Re-uploading every screenshot of the session on every turn would cost a
    // fortune in image tokens; a follow-up question is only ever about the last
    // couple of them.
    let budget = 2;
    for (let i = turns.length - 1; i >= 0; i--) {
      if (turns[i].images.length === 0) continue;
      if (budget > 0) budget -= 1;
      else turns[i] = { ...turns[i], images: [] };
    }
    return turns;
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    setAttachNote('');
  }, []);

  /**
   * Take pasted, dropped or picked files and turn them into attachments.
   * Downscaling happens here so the request stays small even when the admin
   * drops a 4000px phone photo.
   */
  const addFiles = useCallback(async (files) => {
    const images = Array.from(files || []).filter(isImageFile);
    if (images.length === 0) return;

    setIsOpen(true);
    setAttachNote('');
    setIsAttaching(true);
    try {
      const prepared = await prepareAttachments(images);
      if (prepared.length === 0) {
        setAttachNote('That file could not be read as an image.');
        return;
      }
      const room = MAX_ATTACHMENTS - attachmentsRef.current.length;
      if (prepared.length > room) {
        setAttachNote(`Up to ${MAX_ATTACHMENTS} images per message.`);
      }
      const withIds = prepared.slice(0, Math.max(0, room)).map((image, i) => ({
        ...image,
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`
      }));
      setAttachments((prev) => [...prev, ...withIds]);
    } catch (error) {
      console.error('Attachment failed:', error);
      setAttachNote(error.message || 'That image could not be attached.');
    } finally {
      setIsAttaching(false);
    }
  }, []);

  const removeAttachment = useCallback((id) => {
    setAttachments((prev) => prev.filter((image) => image.id !== id));
    setAttachNote('');
  }, []);

  /** Handle a normal, pending-action or disambiguation response from the backend */
  const handleBackendResponse = useCallback(async (response) => {
    if (response.reply) {
      appendMessage({
        role: 'assistant',
        text: response.reply,
        lang: response.lang || detectLanguage(response.reply),
        entities: response.entities || [],
        visuals: response.visuals || [],
        toolsUsed: response.toolsUsed || [],
        navigate: response.navigate || null
      });
    }

    // A name matched several users, or none: let the admin pick instead of
    // making them retype or re-record the whole request.
    if (response.disambiguation) {
      appendMessage({
        role: 'assistant',
        type: 'choice',
        choice: response.disambiguation,
        status: 'pending'
      });
      return; // no TTS while waiting for a decision
    }

    if (response.pendingAction) {
      appendMessage({
        role: 'assistant',
        type: 'confirm',
        action: response.pendingAction,
        status: 'pending'
      });
      return; // no TTS while waiting for a decision
    }

    if (response.reply) {
      if (response.audio) {
        speakReply(response.audio, response.reply, response.lang, response.audioMime);
      } else if (voiceEnabledRef.current) {
        try {
          const { audio, audioMime } = await synthesizeSpeech(response.reply, response.lang);
          speakReply(audio, response.reply, response.lang, audioMime);
        } catch {
          speakReply(null, response.reply, response.lang);
        }
      }
    }
  }, [appendMessage, speakReply]);

  /**
   * Ask the assistant.
   * `display` shows something friendlier than the text actually sent, which is
   * how a disambiguation choice reads as "Ahmad Saad" while the model receives
   * an unambiguous instruction. `images` are base64 data URLs the model reads
   * alongside the question.
   */
  const handleAsk = useCallback(async (question, { hidden = false, display = null, images = [] } = {}) => {
    const trimmed = (question || '').trim();
    if (!trimmed && images.length === 0) return;

    stopPlayback();
    appendMessage({ role: 'user', text: trimmed, display, images, lang: detectLanguage(display || trimmed), hidden });
    setInput('');
    clearAttachments();
    setIsTyping(true);
    setBusyLabel(images.length > 0 ? 'Reading' : 'Thinking');

    try {
      const history = [...historyForBackend(), { role: 'user', text: trimmed, images }];
      const response = await sendChat(history);
      setIsTyping(false);
      await handleBackendResponse(response);
    } catch (error) {
      console.error('AI chat backend failed, using local fallback:', error);
      const { text, lang } = answerQuestion(trimmed);
      appendMessage({ role: 'assistant', text, lang });
      setIsTyping(false);
      speakReply(null, text, lang);
    }
  }, [appendMessage, clearAttachments, handleBackendResponse, historyForBackend, speakReply, stopPlayback]);

  /** Send whatever is in the composer: text, attachments, or both. */
  const submitComposer = useCallback(() => {
    if (isTyping || isAttaching) return;
    handleAsk(input, { images: attachmentsRef.current.map((image) => image.dataUrl) });
  }, [handleAsk, input, isAttaching, isTyping]);

  /** The admin picked one of the candidates offered for an ambiguous name. */
  const handleChooseCandidate = useCallback((messageIndex, candidate) => {
    const msg = messagesRef.current[messageIndex];
    if (!msg || msg.status !== 'pending') return;

    setMessages((prev) => prev.map((m, i) => (i === messageIndex ? { ...m, status: 'chosen', chosen: candidate.value } : m)));
    handleAsk(
      `[SYSTEM] For "${msg.choice?.query || 'the name I asked about'}" the admin means exactly "${candidate.value}". Use that and continue with what they asked.`,
      { display: `${candidate.label}${candidate.value !== candidate.label ? ` (${candidate.value})` : ''}` }
    );
  }, [handleAsk]);

  /** Start over: clears the transcript on screen and in storage. */
  const resetConversation = useCallback(() => {
    stopPlayback();
    setMessages([WELCOME_MESSAGE]);
    setInput('');
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable — the in-memory reset is what matters */
    }
  }, [stopPlayback]);

  /** Admin tapped Confirm on a pending write action */
  const handleConfirmAction = useCallback(async (messageIndex) => {
    const msg = messagesRef.current[messageIndex];
    if (!msg || msg.status !== 'pending') return;

    setMessages((prev) => prev.map((m, i) => (i === messageIndex ? { ...m, status: 'working' } : m)));
    setIsTyping(true);

    try {
      const { result } = await executeTool(msg.action.tool, msg.action.input);
      setMessages((prev) => prev.map((m, i) => (i === messageIndex ? { ...m, status: 'confirmed' } : m)));

      const systemNote = `[SYSTEM] The admin CONFIRMED the action "${msg.action.summary}". It was executed. Result: ${JSON.stringify(result)}. Briefly report the outcome to the user in their language.`;
      const history = [...historyForBackend(), { role: 'user', text: systemNote }];
      const response = await sendChat(history);
      appendMessage({ role: 'user', text: systemNote, hidden: true });
      setIsTyping(false);
      await handleBackendResponse(response);
    } catch (error) {
      console.error('Tool execution failed:', error);
      setMessages((prev) => prev.map((m, i) => (i === messageIndex ? { ...m, status: 'failed' } : m)));
      appendMessage({
        role: 'assistant',
        text: `The action failed: ${error.message || 'unknown error'}`,
        lang: 'en',
        isError: true
      });
      setIsTyping(false);
    }
  }, [appendMessage, handleBackendResponse, historyForBackend]);

  /** Admin tapped Cancel on a pending write action */
  const handleCancelAction = useCallback((messageIndex) => {
    setMessages((prev) => {
      const next = prev.map((m, i) => (i === messageIndex ? { ...m, status: 'cancelled' } : m));
      next.push({ role: 'user', text: '[SYSTEM] The admin CANCELLED the pending action. Do not execute it.', hidden: true });
      return next;
    });
  }, []);

  const clearRecordTimer = useCallback(() => {
    clearInterval(recordTimerRef.current);
    recordTimerRef.current = null;
    setRecordSeconds(0);
  }, []);

  const processRecording = useCallback(async ({ base64, durationSec, heardSpeech }) => {
    recorderRef.current = null;
    setIsRecording(false);
    setCancelArmed(false);
    clearRecordTimer();
    levelRef.current = 0;

    const lang = micLangRef.current;
    const nothingHeard = durationSec < 0.5 || heardSpeech === false;
    if (nothingHeard) {
      if (durationSec >= 0.5) {
        appendMessage({
          role: 'assistant',
          text: lang === 'ar' ? 'Ma sme3et shi. Jarrib 2erreb el mic w 7ke marra tenye.' : "I didn't hear anything — try moving closer to the mic and speaking again.",
          lang: lang === 'ar' ? 'ar' : 'en',
          isError: true
        });
      }
      return;
    }

    setIsTyping(true);
    setBusyLabel('Listening');
    // A screenshot attached before talking is part of the same question
    const images = attachmentsRef.current.map((image) => image.dataUrl);
    try {
      const result = await sendVoice(base64, lang, historyForBackend(), voiceEnabledRef.current, images);

      if (!result.transcript) {
        appendMessage({
          role: 'assistant',
          text: lang === 'ar' ? 'Ma sme3et shi wade7, jarrib marra tenye.' : "I couldn't make that out — please try again.",
          lang: lang === 'ar' ? 'ar' : 'en',
          isError: true
        });
        setIsTyping(false);
        return;
      }

      appendMessage({
        role: 'user',
        text: result.transcript,
        images,
        lang: detectLanguage(result.transcript),
        fromVoice: true,
        corrections: result.corrections || []
      });
      clearAttachments();
      setBusyLabel('Thinking');
      setIsTyping(false);
      await handleBackendResponse(result);
    } catch (error) {
      console.error('Voice processing failed:', error);
      appendMessage({
        role: 'assistant',
        text: lang === 'ar' ? 'Ma 2dirt e3alej el sawt. Jarrib tektob su2alak.' : 'Voice processing failed. Try typing your question.',
        lang: lang === 'ar' ? 'ar' : 'en',
        isError: true
      });
      setIsTyping(false);
    }
  }, [appendMessage, clearAttachments, clearRecordTimer, handleBackendResponse, historyForBackend]);

  const startRecording = useCallback(async () => {
    if (recorderRef.current) return;
    stopPlayback();
    setCancelArmed(false);
    try {
      const recorder = new PcmRecorder({
        maxSeconds: MAX_RECORD_SECONDS,
        onStop: processRecording,
        onLevel: (level) => { levelRef.current = level; }
      });
      await recorder.start();
      recorderRef.current = recorder;
      setIsRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch (error) {
      console.error('Microphone access failed:', error);
      appendMessage({
        role: 'assistant',
        text: 'Could not access the microphone. Please allow microphone permission in your browser.',
        lang: 'en',
        isError: true
      });
    }
  }, [appendMessage, processRecording, stopPlayback]);

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder?.recording) {
      recorder.finish(); // hands the audio to processRecording
      return;
    }
    recorderRef.current = null;
    setIsRecording(false);
    clearRecordTimer();
  }, [clearRecordTimer]);

  const cancelRecording = useCallback(() => {
    recorderRef.current?.cancel();
    recorderRef.current = null;
    setIsRecording(false);
    setCancelArmed(false);
    clearRecordTimer();
    levelRef.current = 0;
  }, [clearRecordTimer]);

  // --- Microphone gestures -------------------------------------------------
  // Tap to start and let silence end it, or hold to talk and release to send.
  // Dragging away from the button while holding cancels instead.

  const HOLD_THRESHOLD_MS = 450;
  const CANCEL_DISTANCE_PX = 70;

  const handleMicPointerDown = useCallback((event) => {
    if (isTyping) return;
    if (recorderRef.current) {
      stopRecording(); // tapping again while recording sends it
      return;
    }
    pressRef.current = { x: event.clientX, y: event.clientY, at: Date.now() };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    startRecording();
  }, [isTyping, startRecording, stopRecording]);

  const handleMicPointerMove = useCallback((event) => {
    const press = pressRef.current;
    if (!press) return;
    const movedAway = (press.x - event.clientX > CANCEL_DISTANCE_PX) || (press.y - event.clientY > CANCEL_DISTANCE_PX);
    if (movedAway !== cancelArmedRef.current) setCancelArmed(movedAway);
  }, []);

  const handleMicPointerUp = useCallback(() => {
    const press = pressRef.current;
    pressRef.current = null;
    if (!press) return;

    if (cancelArmedRef.current) {
      cancelRecording();
      return;
    }
    // A quick tap leaves it recording; a deliberate hold sends on release
    if (Date.now() - press.at >= HOLD_THRESHOLD_MS) stopRecording();
  }, [cancelRecording, stopRecording]);

  const closePanel = useCallback(() => {
    cancelRecording();
    stopPlayback();
    setIsOpen(false);
  }, [cancelRecording, stopPlayback]);

  /** Close the panel and go somewhere in the app */
  const openRoute = useCallback((path) => {
    closePanel();
    navigate(path);
  }, [closePanel, navigate]);

  /** Open a user's deep view page from an entity chip */
  const openDeepView = useCallback(
    (username) => openRoute(`/users/${encodeURIComponent(username)}/deep-view`),
    [openRoute]
  );

  // Cleanup on unmount
  useEffect(() => () => {
    recorderRef.current?.cancel();
    playingAudioRef.current?.pause();
    window.speechSynthesis?.cancel();
    clearInterval(recordTimerRef.current);
  }, []);

  // Keep the conversation across page navigation and reloads
  useEffect(() => {
    saveConversation(messages);
  }, [messages]);

  // Ctrl+V a screenshot straight into the conversation — the fastest way to
  // show the assistant what you are looking at.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onPaste = (event) => {
      const images = imagesFromDataTransfer(event.clipboardData);
      if (images.length === 0) return;
      event.preventDefault();
      addFiles(images);
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [addFiles, isOpen]);

  const handleDragOver = useCallback((event) => {
    if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
    event.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    // Ignore the events fired while moving between children of the panel
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    setIsDraggingOver(false);
    addFiles(imagesFromDataTransfer(event.dataTransfer));
  }, [addFiles]);

  // Hold space to talk on desktop, as long as focus is not in a text field
  useEffect(() => {
    if (!isOpen) return undefined;

    const isTextField = (el) => !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

    const onKeyDown = (event) => {
      if (event.code !== 'Space' || event.repeat || isTextField(event.target)) return;
      event.preventDefault();
      if (!recorderRef.current) startRecording();
    };
    const onKeyUp = (event) => {
      if (event.code !== 'Space' || isTextField(event.target)) return;
      event.preventDefault();
      if (recorderRef.current) stopRecording();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [isOpen, startRecording, stopRecording]);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when panel opens (desktop only — avoids popping the mobile keyboard)
  useEffect(() => {
    if (isOpen && window.innerWidth >= 640) inputRef.current?.focus();
  }, [isOpen]);

  // Lock body scroll while the full-screen sheet is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 640) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  if (!allowed || !unlocked) return null;

  return (
    <>
      {/* Floating button — above the bottom TabBar */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] right-4 z-[9990] flex items-center justify-center w-14 h-14 rounded-2xl
                     bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30
                     hover:scale-110 hover:shadow-xl active:scale-95 transition-all duration-200"
          title="AI Panel Assistant"
          aria-label="Open AI assistant"
        >
          <SparklesIcon className="w-7 h-7" />
          <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-gradient-to-br from-purple-400 to-pink-500 border-2 border-white dark:border-gray-900"></span>
          </span>
        </button>
      )}

      {/* Chat panel: full-screen sheet on phones, floating card on desktop */}
      {isOpen && (
        <div
          dir="ltr"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className="fixed inset-0 z-[9990] flex flex-col bg-gray-50 dark:bg-gray-900 overflow-hidden
                     sm:inset-auto sm:bottom-20 sm:right-4 sm:w-[26rem] sm:h-[34rem]
                     sm:max-h-[calc(100vh-7rem)] sm:rounded-3xl sm:shadow-2xl
                     sm:border sm:border-gray-200 sm:dark:border-gray-700"
        >
          {isDraggingOver && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2
                            bg-indigo-500/10 backdrop-blur-sm border-2 border-dashed border-indigo-400
                            sm:rounded-3xl pointer-events-none">
              <PhotoIcon className="w-10 h-10 text-indigo-500" />
              <span className="text-sm font-bold text-indigo-600 dark:text-indigo-300">Drop the screenshot here</span>
            </div>
          )}
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white shadow-md"
               style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <SparklesIcon className="w-5 h-5" />
              </div>
              <div className="leading-tight">
                <div className="font-bold text-[15px]">Panel Assistant</div>
                <div className="text-[11px] opacity-75">GGR · limits · players · voice</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setMicLang((current) => {
                  const index = MIC_LANGS.findIndex((l) => l.id === current);
                  return MIC_LANGS[(index + 1) % MIC_LANGS.length].id;
                })}
                className="px-2.5 py-1.5 text-xs font-bold rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                title={`Microphone language: ${(MIC_LANGS.find((l) => l.id === micLang) || MIC_LANGS[0]).title}`}
              >
                {(MIC_LANGS.find((l) => l.id === micLang) || MIC_LANGS[0]).label}
              </button>
              <button
                onClick={resetConversation}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                title="Start a new conversation"
                aria-label="New conversation"
              >
                <ArrowPathIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setVoiceEnabled((v) => {
                    if (v) stopPlayback();
                    return !v;
                  });
                }}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                title={voiceEnabled ? 'Mute voice replies' : 'Enable voice replies'}
              >
                {voiceEnabled ? <SpeakerWaveIcon className="w-5 h-5" /> : <SpeakerXMarkIcon className="w-5 h-5 opacity-60" />}
              </button>
              <button
                onClick={closePanel}
                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Close assistant"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-3.5 py-4 space-y-3">
            {messages.map((msg, i) => {
              if (msg.hidden) return null;

              // "Which one did you mean?" — offered whenever a name matched
              // more than one user, or none at all.
              if (msg.type === 'choice') {
                const candidates = msg.choice?.candidates || [];
                return (
                  <div key={i} className="flex justify-start">
                    <div className="w-full rounded-2xl border-2 border-sky-300 dark:border-sky-700 bg-sky-50 dark:bg-sky-900/30 overflow-hidden shadow-md">
                      <div className="flex items-center gap-2 px-3.5 py-2.5 bg-sky-100 dark:bg-sky-900/50">
                        <QuestionMarkCircleIcon className="w-5 h-5 text-sky-600 dark:text-sky-400 shrink-0" />
                        <span className="text-xs font-bold uppercase tracking-wide text-sky-700 dark:text-sky-300">
                          Which one?
                        </span>
                      </div>
                      <div className="px-3.5 py-3">
                        <p className="text-[14px] text-gray-800 dark:text-gray-100 leading-snug">
                          {msg.choice?.question}
                        </p>

                        {candidates.length > 0 ? (
                          <div className="mt-3 space-y-1.5">
                            {candidates.map((candidate) => (
                              <button
                                key={candidate.value}
                                disabled={msg.status !== 'pending'}
                                onClick={() => handleChooseCandidate(i, candidate)}
                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left transition-colors ${
                                  msg.chosen === candidate.value
                                    ? 'bg-sky-600 border-sky-600 text-white'
                                    : 'bg-white dark:bg-gray-800 border-sky-200 dark:border-sky-800 hover:bg-sky-100 dark:hover:bg-sky-900/50 disabled:opacity-50'
                                }`}
                              >
                                <UserCircleIcon className="w-5 h-5 shrink-0 opacity-60" />
                                <span className="min-w-0 flex-1">
                                  <span className="block text-[14px] font-semibold truncate">{candidate.label}</span>
                                  {candidate.sublabel && (
                                    <span className="block text-[11px] opacity-70 truncate">{candidate.sublabel}</span>
                                  )}
                                </span>
                                {candidate.value !== candidate.label && (
                                  <span className="text-[11px] font-mono opacity-60 shrink-0">{candidate.value}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            Nothing close enough to suggest — try the name as it appears in the panel.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // Confirmation popup card for write actions
              if (msg.type === 'confirm') {
                return (
                  <div key={i} className="flex justify-start">
                    <div className="w-full rounded-2xl border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/30 overflow-hidden shadow-md">
                      <div className="flex items-center gap-2 px-3.5 py-2.5 bg-amber-100 dark:bg-amber-900/50">
                        <ShieldExclamationIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span className="text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                          Confirmation required
                        </span>
                      </div>
                      <div className="px-3.5 py-3">
                        <p className="text-[15px] text-gray-800 dark:text-gray-100 font-semibold break-words leading-snug">
                          {msg.action.summary}
                        </p>
                        {msg.status === 'pending' && (
                          <div className="flex gap-2 mt-3.5">
                            <button
                              onClick={() => handleConfirmAction(i)}
                              className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-[15px] font-bold transition-colors shadow-sm"
                            >
                              <CheckIcon className="w-5 h-5" /> Confirm
                            </button>
                            <button
                              onClick={() => handleCancelAction(i)}
                              className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-[15px] font-bold transition-colors"
                            >
                              <XMarkIcon className="w-5 h-5" /> Cancel
                            </button>
                          </div>
                        )}
                        {msg.status === 'working' && (
                          <p className="mt-2.5 text-sm font-semibold text-amber-700 dark:text-amber-300 animate-pulse">Executing…</p>
                        )}
                        {msg.status === 'confirmed' && (
                          <p className="mt-2.5 text-sm font-bold text-green-700 dark:text-green-400">✓ Confirmed and executed</p>
                        )}
                        {msg.status === 'cancelled' && (
                          <p className="mt-2.5 text-sm font-bold text-gray-500 dark:text-gray-400">✕ Cancelled</p>
                        )}
                        {msg.status === 'failed' && (
                          <p className="mt-2.5 text-sm font-bold text-red-600 dark:text-red-400">✕ Failed</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  {/* Screenshots the admin attached to this question */}
                  {(msg.images || []).length > 0 && (
                    <div className={`mb-1.5 flex flex-wrap gap-1.5 max-w-[88%] ${msg.role === 'user' ? 'justify-end' : ''}`}>
                      {msg.images.map((src, ii) => (
                        <button
                          key={ii}
                          onClick={() => setPreview(src)}
                          className="rounded-xl overflow-hidden border border-indigo-200 dark:border-indigo-800
                                     shadow-sm hover:opacity-90 active:scale-95 transition-all"
                          title="Tap to enlarge"
                        >
                          <img src={src} alt="attachment" className="h-28 w-28 object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Images are not persisted, so a restored turn just says how many */}
                  {!(msg.images || []).length && msg.imageCount > 0 && (
                    <div className="mb-1 text-[11px] text-gray-400 dark:text-gray-500">
                      {msg.imageCount} image{msg.imageCount > 1 ? 's' : ''} attached
                    </div>
                  )}

                  {(msg.display || msg.text) && (
                    <div
                      dir={msg.lang === 'ar' ? 'rtl' : 'ltr'}
                      className={`max-w-[88%] px-4 py-2.5 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                        msg.role === 'user'
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-br-md shadow-md shadow-indigo-500/20'
                          : msg.isError
                            ? 'bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-bl-md'
                            : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-md shadow-sm'
                      }`}
                    >
                      {msg.display || msg.text}
                    </div>
                  )}

                  {/* What the assistant heard, when it corrected a name itself */}
                  {(msg.corrections || []).length > 0 && (
                    <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                      heard {msg.corrections.map((c) => `"${c.from}" as ${c.to}`).join(', ')}
                    </div>
                  )}

                  {/* Fix a misheard word without recording the whole thing again */}
                  {msg.fromVoice && msg.role === 'user' && (
                    <button
                      onClick={() => {
                        setInput(msg.text);
                        inputRef.current?.focus();
                      }}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-indigo-500 transition-colors"
                    >
                      <PencilSquareIcon className="w-3.5 h-3.5" /> edit and resend
                    </button>
                  )}

                  {/* What the assistant actually looked at to answer */}
                  {(msg.toolsUsed || []).length > 0 && (
                    <div className="mt-1.5 flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500">
                      <WrenchScrewdriverIcon className="w-3 h-3 shrink-0" />
                      <span className="truncate">checked {msg.toolsUsed.map((t) => t.replace(/_/g, ' ')).join(', ')}</span>
                    </div>
                  )}

                  {/* Visual data cards */}
                  {(msg.visuals || []).length > 0 && (
                    <div className="mt-2 w-full max-w-[95%] space-y-2">
                      {msg.visuals.map((visual, vi) => {
                        const Renderer = VISUAL_RENDERERS[visual.kind];
                        return Renderer ? <Renderer key={vi} data={visual.data} /> : null;
                      })}
                    </div>
                  )}

                  {/* Jump straight to the page the assistant was asked to open */}
                  {msg.navigate && (
                    <button
                      onClick={() => openRoute(msg.navigate.path)}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl
                                 bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-[13px] font-bold
                                 shadow-md shadow-indigo-500/20 active:scale-95 transition-all"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      Open {msg.navigate.label}
                    </button>
                  )}

                  {/* Deep-view profile links */}
                  {(msg.entities || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 max-w-[95%]">
                      {msg.entities.map((entity) => (
                        <button
                          key={entity.username}
                          onClick={() => openDeepView(entity.username)}
                          className="inline-flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full
                                     bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-700
                                     text-indigo-700 dark:text-indigo-300 text-[13px] font-semibold
                                     hover:bg-indigo-100 dark:hover:bg-indigo-900/70 active:scale-95 transition-all"
                          title={`Open deep view of ${entity.username}`}
                        >
                          <UserCircleIcon className="w-4 h-4" />
                          {entity.username}
                          {entity.type && <span className="text-[10px] font-normal opacity-60">{entity.type}</span>}
                        </button>
                      ))}
                      <button
                        onClick={() => openRoute('/limits')}
                        className="inline-flex items-center gap-1.5 pl-2 pr-3 py-1.5 rounded-full
                                   bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-700
                                   text-amber-700 dark:text-amber-300 text-[13px] font-semibold
                                   hover:bg-amber-100 dark:hover:bg-amber-900/70 active:scale-95 transition-all"
                        title="Open the Limits page"
                      >
                        <ScaleIcon className="w-4 h-4" />
                        Limits page
                      </button>
                    </div>
                  )}

                  {/* Suggested next steps, only under the newest reply */}
                  {i === messages.length - 1 && !isTyping && followUpsFor(msg).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 max-w-[95%]">
                      {followUpsFor(msg).map((chip) => (
                        <button
                          key={chip.label}
                          onClick={() => handleAsk(chip.prompt)}
                          className="px-3 py-1.5 rounded-full border border-dashed border-gray-300 dark:border-gray-600
                                     text-gray-600 dark:text-gray-300 text-[12px] font-medium
                                     hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300
                                     active:scale-95 transition-all"
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* First-run suggestions so the panel is not a blank prompt */}
            {messages.length <= 1 && !isTyping && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {STARTER_PROMPTS.map((chip) => (
                  <button
                    key={chip.label}
                    onClick={() => handleAsk(chip.prompt)}
                    className="px-3 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                               text-gray-700 dark:text-gray-200 text-[13px] font-medium shadow-sm
                               hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300
                               active:scale-95 transition-all"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-2xl rounded-bl-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 rounded-full bg-pink-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  {busyLabel && <span className="text-xs text-gray-400 dark:text-gray-500">{busyLabel}…</span>}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input / recording row */}
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
               style={{ paddingBottom: 'max(0.625rem, env(safe-area-inset-bottom))' }}>
            {isRecording ? (
              /* Recording bar */
              <div className="px-4 pt-3 pb-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={cancelRecording}
                    className="p-2.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors shrink-0"
                    aria-label="Cancel recording"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                  <div className={`flex-1 min-w-0 flex items-center gap-3 px-4 py-2.5 rounded-2xl border transition-colors ${
                    cancelArmed
                      ? 'bg-gray-100 dark:bg-gray-700/60 border-gray-300 dark:border-gray-600'
                      : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
                  }`}>
                    <span className="relative flex h-3 w-3 shrink-0">
                      {!cancelArmed && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>}
                      <span className={`relative inline-flex rounded-full h-3 w-3 ${cancelArmed ? 'bg-gray-400' : 'bg-red-500'}`}></span>
                    </span>
                    {cancelArmed ? (
                      <span className="flex-1 text-sm font-semibold text-gray-500 dark:text-gray-400">Release to cancel</span>
                    ) : (
                      <VoiceWaveform levelRef={levelRef} />
                    )}
                    <span className="text-sm font-bold text-red-600 dark:text-red-300 tabular-nums shrink-0">
                      {Math.floor(recordSeconds / 60)}:{String(recordSeconds % 60).padStart(2, '0')}
                    </span>
                  </div>
                  <button
                    onClick={stopRecording}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-[15px] font-bold shadow-md shadow-indigo-500/25 active:scale-95 transition-all shrink-0"
                  >
                    <PaperAirplaneIcon className="w-5 h-5" /> Send
                  </button>
                </div>
                <p className="mt-1.5 text-center text-[11px] text-gray-400 dark:text-gray-500">
                  Stops on its own when you finish speaking · slide away to cancel
                </p>
              </div>
            ) : (
              /* Text input row */
              <div className="px-3.5 pt-2.5 pb-1">
                {/* Screenshots staged for the next message */}
                {(attachments.length > 0 || isAttaching || attachNote) && (
                  <div className="mb-2 flex items-center gap-2 flex-wrap">
                    {attachments.map((image) => (
                      <div key={image.id} className="relative group">
                        <img
                          src={image.dataUrl}
                          alt={image.name || 'attachment'}
                          onClick={() => setPreview(image.dataUrl)}
                          className="h-16 w-16 object-cover rounded-xl border border-gray-200 dark:border-gray-600 cursor-zoom-in"
                        />
                        <button
                          onClick={() => removeAttachment(image.id)}
                          className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-gray-800 text-white
                                     shadow-md hover:bg-red-500 transition-colors"
                          aria-label="Remove image"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    {isAttaching && (
                      <div className="h-16 w-16 rounded-xl border border-dashed border-gray-300 dark:border-gray-600
                                      flex items-center justify-center">
                        <span className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin" />
                      </div>
                    )}
                    {attachNote && (
                      <span className="text-[11px] text-amber-600 dark:text-amber-400">{attachNote}</span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2">
                <button
                  onPointerDown={handleMicPointerDown}
                  onPointerMove={handleMicPointerMove}
                  onPointerUp={handleMicPointerUp}
                  onPointerCancel={handleMicPointerUp}
                  onContextMenu={(e) => e.preventDefault()}
                  disabled={isTyping}
                  className="p-3 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white
                             shadow-md shadow-indigo-500/25 hover:shadow-lg active:scale-95 transition-all
                             disabled:opacity-40 disabled:shadow-none shrink-0 touch-none select-none"
                  title="Tap to talk, or hold and release to send (space bar works too)"
                >
                  <MicrophoneIcon className="w-5 h-5" />
                </button>
                {/* On phones this opens the photo library or the camera */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isTyping || attachments.length >= MAX_ATTACHMENTS}
                  className="p-2.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300
                             hover:bg-gray-200 dark:hover:bg-gray-600 active:scale-95 transition-all
                             disabled:opacity-40 shrink-0"
                  title="Attach a screenshot or photo"
                  aria-label="Attach an image"
                >
                  <PhotoIcon className="w-5 h-5" />
                </button>
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitComposer(); }}
                  dir={detectLanguage(input) === 'ar' ? 'rtl' : 'ltr'}
                  placeholder={attachments.length > 0 ? 'Ask about this image…' : 'Ask about the panel…'}
                  className="flex-1 min-w-0 px-4 py-2.5 text-base sm:text-[15px] rounded-2xl border border-gray-300 dark:border-gray-600
                             bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100
                             placeholder-gray-400 dark:placeholder-gray-500
                             focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
                />
                <button
                  onClick={submitComposer}
                  disabled={(!input.trim() && attachments.length === 0) || isTyping || isAttaching}
                  className="p-3 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white
                             shadow-md shadow-indigo-500/25 hover:shadow-lg active:scale-95 transition-all
                             disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed shrink-0"
                  aria-label="Send"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
                </div>
              </div>
            )}
          </div>

          {/* Full-size look at an attachment */}
          {preview && (
            <div
              onClick={() => setPreview(null)}
              className="fixed inset-0 z-[9995] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            >
              <img src={preview} alt="attachment" className="max-h-full max-w-full rounded-2xl shadow-2xl" />
              <button
                onClick={() => setPreview(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors"
                aria-label="Close image"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AIAssistant;
